import './landing.css'
import * as THREE from 'three'

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
const canvas = document.querySelector('#revx-hero-scene')

document.querySelector('#year').textContent = String(new Date().getFullYear())

function revealOnScroll() {
  const items = [...document.querySelectorAll('[data-reveal]')]
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.16 },
  )
  items.forEach((item) => observer.observe(item))
}

function countMetrics() {
  const counters = [...document.querySelectorAll('[data-count]')]
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target
        const target = Number(el.dataset.count || 0)
        const start = performance.now()
        const duration = reduceMotion ? 1 : 900
        const tick = (time) => {
          const progress = Math.min(1, (time - start) / duration)
          el.textContent = String(Math.round(target * (1 - Math.pow(1 - progress, 3))))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
        observer.unobserve(el)
      })
    },
    { threshold: 0.45 },
  )
  counters.forEach((counter) => observer.observe(counter))
}

function bindMagneticCards() {
  if (reduceMotion) return
  document.querySelectorAll('.profile-card, .bento-card, .button').forEach((el) => {
    el.addEventListener('pointermove', (event) => {
      const rect = el.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 10
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 10
      el.style.setProperty('--tilt-x', `${-y.toFixed(2)}deg`)
      el.style.setProperty('--tilt-y', `${x.toFixed(2)}deg`)
    })
    el.addEventListener('pointerleave', () => {
      el.style.setProperty('--tilt-x', '0deg')
      el.style.setProperty('--tilt-y', '0deg')
    })
  })
}

function buildHeroScene() {
  if (!canvas) return

  let renderer
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    })
  } catch {
    document.body.classList.add('no-webgl')
    return
  }

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 120)
  camera.position.set(0, 0.25, 8)

  const cyan = new THREE.Color('#21f6ff')
  const ember = new THREE.Color('#ff6a2a')
  const blue = new THREE.Color('#5f7dff')

  const ringMaterial = new THREE.MeshBasicMaterial({
    color: cyan,
    transparent: true,
    opacity: 0.52,
    blending: THREE.AdditiveBlending,
  })
  const emberMaterial = new THREE.MeshBasicMaterial({
    color: ember,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
  })
  const group = new THREE.Group()
  scene.add(group)

  for (let i = 0; i < 5; i += 1) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.25 + i * 0.34, 0.006 + i * 0.001, 8, 192),
      i === 4 ? emberMaterial : ringMaterial,
    )
    ring.rotation.x = Math.PI / 2.35
    ring.rotation.z = i * 0.18
    ring.position.y = -0.1
    group.add(ring)
  }

  const core = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.56, 1),
    new THREE.MeshBasicMaterial({
      color: '#e8ffff',
      wireframe: true,
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending,
    }),
  )
  group.add(core)

  const needle = new THREE.Mesh(
    new THREE.BoxGeometry(0.035, 1.85, 0.035),
    new THREE.MeshBasicMaterial({
      color: '#eaffff',
      transparent: true,
      opacity: 0.78,
      blending: THREE.AdditiveBlending,
    }),
  )
  needle.position.set(0.56, 0.38, 0)
  needle.rotation.z = -0.74
  group.add(needle)

  const starGeometry = new THREE.BufferGeometry()
  const starCount = 900
  const positions = new Float32Array(starCount * 3)
  const colors = new Float32Array(starCount * 3)
  for (let i = 0; i < starCount; i += 1) {
    const index = i * 3
    positions[index] = (Math.random() - 0.5) * 18
    positions[index + 1] = (Math.random() - 0.5) * 10
    positions[index + 2] = -Math.random() * 18
    const color = i % 9 === 0 ? ember : i % 5 === 0 ? blue : cyan
    colors[index] = color.r
    colors[index + 1] = color.g
    colors[index + 2] = color.b
  }
  starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  const stars = new THREE.Points(
    starGeometry,
    new THREE.PointsMaterial({
      size: 0.025,
      vertexColors: true,
      transparent: true,
      opacity: 0.72,
      blending: THREE.AdditiveBlending,
    }),
  )
  scene.add(stars)

  const pulses = []
  for (let i = 0; i < 9; i += 1) {
    const pulse = new THREE.Mesh(new THREE.TorusGeometry(0.16 + i * 0.03, 0.006, 6, 48), ringMaterial)
    pulse.position.set(-3.4 + i * 0.85, -2.15 + Math.sin(i) * 0.08, -0.8)
    pulse.rotation.x = Math.PI / 2
    pulse.scale.setScalar(0.7 + (i % 3) * 0.18)
    group.add(pulse)
    pulses.push(pulse)
  }

  function resize() {
    const width = window.innerWidth
    const height = Math.max(window.innerHeight, 680)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8))
    renderer.setSize(width, height, false)
    camera.aspect = width / height
    camera.updateProjectionMatrix()
  }
  resize()
  window.addEventListener('resize', resize)

  let pointerX = 0
  let pointerY = 0
  window.addEventListener('pointermove', (event) => {
    pointerX = (event.clientX / window.innerWidth - 0.5) * 0.35
    pointerY = (event.clientY / window.innerHeight - 0.5) * 0.22
  })

  function frame(time = 0) {
    const seconds = time * 0.001
    group.rotation.y = pointerX + Math.sin(seconds * 0.28) * 0.08
    group.rotation.x = -0.18 + pointerY + Math.sin(seconds * 0.18) * 0.04
    group.children.forEach((child, index) => {
      if (child.geometry?.type === 'TorusGeometry') child.rotation.z += 0.0016 + index * 0.00012
    })
    core.rotation.x += 0.004
    core.rotation.y += 0.006
    needle.rotation.z = -0.86 + Math.sin(seconds * 1.15) * 0.32
    stars.rotation.y += 0.00045
    pulses.forEach((pulse, index) => {
      pulse.material.opacity = 0.22 + Math.abs(Math.sin(seconds * 3.4 + index * 0.8)) * 0.42
      pulse.scale.setScalar(0.75 + Math.sin(seconds * 2.2 + index) * 0.16)
    })
    renderer.render(scene, camera)
    window.__revxLandingSceneRendered = true
    if (!reduceMotion) requestAnimationFrame(frame)
  }

  requestAnimationFrame(frame)
}

buildHeroScene()
revealOnScroll()
countMetrics()
bindMagneticCards()
